// ============================================================
// ModerationPage — MEN-239 [V1-11]
// File de modération : signalements filtrables par statut,
// transition de statut (pending → investigating → resolved/rejected).
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { AdminReport } from '@siverix/api-client';
import { ErrorBox } from './OverviewPage';

const STATUSES: AdminReport['status'][] = ['pending', 'investigating', 'resolved', 'rejected'];

const STATUS_LABEL: Record<AdminReport['status'], string> = {
  pending: 'En attente',
  investigating: 'En cours',
  resolved: 'Résolu',
  rejected: 'Rejeté',
};

export function ModerationPage(): JSX.Element {
  const [rows, setRows] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AdminReport['status'] | ''>('pending');

  const fetchRows = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const data = await api.adminListReports({
        status: statusFilter || undefined,
        limit: 100,
      });
      setRows(data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  async function setStatus(id: string, status: AdminReport['status']): Promise<void> {
    try {
      await api.adminUpdateReport(id, status);
      await fetchRows();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Modération</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        File des signalements émis par les utilisateurs. Traitez chaque entrée et faites évoluer son
        statut.
      </p>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '14px' }}>
        <label style={lbl}>
          Statut
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AdminReport['status'] | '')}
            style={inp}
          >
            <option value="">Tous</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <button onClick={() => void fetchRows()} style={btnGhost} type="button">
          Rafraîchir
        </button>
      </div>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={th}>Date</th>
                <th style={th}>Rapporteur</th>
                <th style={th}>Cible</th>
                <th style={th}>Motif</th>
                <th style={th}>Statut</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={td}>{new Date(r.createdAt).toLocaleString('fr-FR')}</td>
                  <td style={td}>{r.reporter?.username ?? r.reporterId}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace, monospace' }}>
                    {r.targetType}:{r.targetId}
                  </td>
                  <td style={{ ...td, maxWidth: 320, whiteSpace: 'pre-wrap' }}>{r.reason}</td>
                  <td style={td}>
                    <span style={badge(r.status)}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.status !== 'investigating' && (
                      <button
                        onClick={() => void setStatus(r.id, 'investigating')}
                        style={btnGhost}
                        type="button"
                      >
                        Enquêter
                      </button>
                    )}{' '}
                    {r.status !== 'resolved' && (
                      <button
                        onClick={() => void setStatus(r.id, 'resolved')}
                        style={btn}
                        type="button"
                      >
                        Résoudre
                      </button>
                    )}{' '}
                    {r.status !== 'rejected' && (
                      <button
                        onClick={() => void setStatus(r.id, 'rejected')}
                        style={btnDanger}
                        type="button"
                      >
                        Rejeter
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      ...td,
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      padding: '24px',
                    }}
                  >
                    Aucun signalement.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function badge(status: AdminReport['status']): React.CSSProperties {
  const color =
    status === 'pending'
      ? 'var(--color-warning, #d29922)'
      : status === 'investigating'
        ? 'var(--color-accent)'
        : status === 'resolved'
          ? 'var(--color-success, #3fb950)'
          : 'var(--color-error, #f85149)';
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    border: `1px solid ${color}`,
    color,
  };
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '8px 16px' };
const lbl: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const inp: React.CSSProperties = {
  padding: '6px 8px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btn: React.CSSProperties = {
  padding: '5px 12px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  ...btn,
  background: 'var(--color-error, #f85149)',
};
const btnGhost: React.CSSProperties = {
  padding: '5px 10px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};
